import React, { useState, useEffect, useMemo, useCallback } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import Accordion from "react-bootstrap/Accordion";
import TaskResultsTable2 from "./TaskResultsTable2.js";
import secondsToTime from "../services/secondsToTime";
import "./ResultsHistory.css";
import DeleteAnswerModal from "./DeleteAnswerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { getNotes } from "../services/APIClient";

import { preprocessStudentResults } from "../services/GroupAndAverageTasks";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";

function AllResultsHistory(props) {
  const {
    studentResults,
    isPrintOut,
    detailedView,
    teacherid,
    schoolid,
    students,
    detailedRef,
    overviewRef,
    hideAccordion, // New prop to control accordion visibility
    viewParent, // New prop to control parent view
    viewTeacher, // New prop to control teacher view
    currentViewRef, // New prop to track the current view
    classDetails, // New prop for class details
    teachers,
  } = props;

  console.log("AllResultsHistory studentResults:", studentResults);
  console.log("AllResultsHistory students:", students);
  console.log(props)

  const [assessmentResults, setAssessmentResults] = useState([]);
  const [visibleAccordions, setVisibleAccordions] = useState(new Set());
  const [visibleNestedAccordions, setVisibleNestedAccordions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [modalAssessment, setModalAssessment] = useState(null);
  const [answerIds, setAnswerIds] = useState([]);
  const [isRetakeFullAssessment, setIsRetakeFullAssessment] = useState(false);
  const [answerId, setAnswerId] = useState(null);
  const [studentNotes, setStudentNotes] = useState({});

  const handleModalClose = () => setShowModal(false);

  const formatNoteDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("en-IE", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Europe/Dublin",
      });
    } catch {
      return dateStr;
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers?.find((t) => t.id === teacherId);
    return teacher?.name || "Unknown";
  };

  const [currentAcademicYear] = useAcademicYearOptions({ previousYears: 0 });
  const academicYear = currentAcademicYear.display;

  const handleRetakeFullAssessment = (assessment) => {
    const allAnswerIds = assessment.tasks.map((task) => task.answerid);
    setModalTask("All Tasks");
    setModalAssessment(assessment.name);
    setAnswerIds(allAnswerIds);
    setShowModal(true);
    setIsRetakeFullAssessment(true);
  };

  const handleDeleteTask = useCallback(
    (event, assessmentIndex, taskIndex) => {
      event.stopPropagation();
      event.preventDefault();

      const assessment = assessmentResults[assessmentIndex];
      const task = assessment.tasks[taskIndex];
      setModalTask(task?.name);
      setModalAssessment(assessment?.name);
      setAnswerId(task?.answerid);
      setShowModal(true);
      setIsRetakeFullAssessment(false);
    },
    [assessmentResults]
  );

  useEffect(() => {
    // Handle case where studentResults might be an array (legacy) or object (expected)
    if (Array.isArray(studentResults)) {
      console.warn('AllResultsHistory received array instead of object - data structure mismatch');
      setAssessmentResults([]);
      return;
    }

    if (!studentResults || typeof studentResults !== 'object') {
      console.warn('AllResultsHistory received invalid studentResults:', studentResults);
      setAssessmentResults([]);
      return;
    }

    // Map over the studentResults to retain its structure
    const updatedStudentResults = Object.keys(studentResults)?.reduce(
      (acc, studentId) => {
        // Find the matching student from the students prop
        const matchingStudent = students?.find(
          (student) => student.id === studentId
        );

        // Ensure studentResults[studentId] is an array before calling .map()
        const studentAssessments = studentResults[studentId];
        const assessmentsWithStudentName = Array.isArray(studentAssessments)
          ? studentAssessments.map((assessment) => ({
            ...assessment,
            studentName: matchingStudent ? matchingStudent.name : "Unknown",
          }))
          : []; // Return empty array if not an array

        // Keep the structure the same
        acc[studentId] = assessmentsWithStudentName;
        return acc;
      },
      {}
    );

    // Flatten the updatedStudentResults to get an array of all assessments
    const allAssessments = Object.values(updatedStudentResults).flat();

    // Debug: Check answers in allAssessments before preprocessing
    console.log('=== AllResultsHistory Debug ===');
    console.log('allAssessments count:', allAssessments.length);
    if (allAssessments[0]) {
      console.log('First assessment:', allAssessments[0]);
      console.log('First assessment tasks count:', allAssessments[0].tasks?.length);
      if (allAssessments[0].tasks?.[0]) {
        console.log('First task in first assessment:', allAssessments[0].tasks[0]);
        console.log('Has answers?', !!allAssessments[0].tasks[0].answers);
        console.log('Answers:', allAssessments[0].tasks[0].answers);
      }
    }

    const processed = preprocessStudentResults(allAssessments);
    console.log('After preprocessing:', processed);
    if (processed[0]) {
      console.log('First processed tasks:', processed[0].tasks);
      if (processed[0].tasks?.[0]) {
        console.log('First processed task answers:', processed[0].tasks[0].answers);
      }
    }

    setAssessmentResults(processed);
  }, [studentResults, students]);

  useEffect(() => {
    if (!students?.length) {
      setStudentNotes({});
      return;
    }

    const notesObj = {};
    Promise.all(
      students.map(async (student) => {
        try {
          const res = await getNotes("student", student.id);
          notesObj[student.id] = res?.data || [];
        } catch {
          notesObj[student.id] = [];
        }
      })
    ).then(() => setStudentNotes(notesObj));
  }, [students]);

  useEffect(() => {
    if (isPrintOut) {
      if (detailedView) {
        // Open all outer and inner accordions for Detailed view
        const allIndexes = new Set(assessmentResults.map((_, index) => index));
        setVisibleAccordions(allIndexes);

        const allNestedAccordions = {};
        assessmentResults.forEach((assessment, assessmentIndex) => {
          assessment.tasks.forEach((_, taskIndex) => {
            const key = `${assessmentIndex}-${taskIndex}`;
            allNestedAccordions[key] = true;
          });
        });
        setVisibleNestedAccordions(allNestedAccordions);
      } else {
        // Open only outer accordions for Simple view
        const allIndexes = new Set(assessmentResults.map((_, index) => index));
        setVisibleAccordions(allIndexes);
        setVisibleNestedAccordions({}); // Close all inner accordions
      }
    } else if (viewParent) {
      // Open the first accordion for all students when viewParent is true
      const firstIndexes = new Set(assessmentResults.map((_, index) => index));
      // setVisibleAccordions(firstIndexes);
      setVisibleAccordions(new Set());
      setVisibleNestedAccordions({});
    } else if (viewTeacher) {
      // Open all accordion items when viewTeacher is true
      const allIndexes = new Set(assessmentResults.map((_, index) => index));
      setVisibleAccordions(allIndexes);
    }
  }, [detailedView, isPrintOut, assessmentResults, viewParent, viewTeacher]);

  useEffect(() => {
    if (viewTeacher) {
      // Open all task accordions for all assessments
      const initialNestedAccordions = {};
      assessmentResults.forEach((assessment, assessmentIndex) => {
        assessment.tasks.forEach((_, taskIndex) => {
          const key = `${assessmentIndex}-${taskIndex}`;
          initialNestedAccordions[key] = true;
        });
      });
      setVisibleNestedAccordions(initialNestedAccordions);
    }
  }, [viewTeacher, assessmentResults]);

  const calculateTotalTime = (groupedTasks) =>
    groupedTasks.reduce((sum, group) => sum + group.aggregated.duration, 0);

  // In AllResultsHistory.js, REPLACE the calculateReadingScore function with this:

  const calculateReadingScore = (assessment) => {
    // First try to use groupedTasks (preferred method)
    if (assessment.groupedTasks && assessment.groupedTasks.length > 0) {
      const totalScore = assessment.groupedTasks.reduce(
        (sum, task) => sum + task.aggregated.score,
        0
      );
      const taskCount = assessment.groupedTasks.length;

      // Return the average score across grouped tasks (multiply by 100 for percentage)
      return Math.round((totalScore / taskCount) * 100);
    }

    // Fallback to individual tasks if groupedTasks not available
    if (assessment.tasks && assessment.tasks.length > 0) {
      const totalScore = assessment.tasks.reduce(
        (sum, task) => sum + task.score,
        0
      );
      const taskCount = assessment.tasks.length;

      // Return the average score across individual tasks (multiply by 100 for percentage)
      return Math.round((totalScore / taskCount) * 100);
    }

    // Default to 0 if no tasks available
    return 0;
  };

  const derivedResults = useMemo(() => {
    return assessmentResults
      .map((assessment, globalAssessmentIndex) => ({
        ...assessment,
        globalAssessmentIndex,
        totalTime: calculateTotalTime(assessment.groupedTasks),
        readingScore: calculateReadingScore(assessment),
      }))
      .filter((assessment) => assessment.studentName !== "Unknown");
  }, [assessmentResults]);

  const taskNames = useMemo(() => {
    const allTaskNames = new Set();
    derivedResults.forEach((assessment) => {
      if (assessment.groupedTasks) {
        assessment.groupedTasks.forEach((task) => {
          allTaskNames.add(task.name);
        });
      }
    });
    return Array.from(allTaskNames);
  }, [derivedResults]);

  // Alternative debug version to help identify what data structure you have:
  const calculateReadingScoreDebug = (assessment) => {
    console.log(`\n=== DEBUG AllResultsHistory: ${assessment.name} ===`);
    console.log("Assessment object:", assessment);
    console.log("Has groupedTasks:", !!assessment.groupedTasks);
    console.log("Has tasks:", !!assessment.tasks);

    if (assessment.groupedTasks) {
      console.log("GroupedTasks length:", assessment.groupedTasks.length);
      console.log("Sample groupedTask:", assessment.groupedTasks[0]);
    }

    if (assessment.tasks) {
      console.log("Tasks length:", assessment.tasks.length);
      console.log("Sample task:", assessment.tasks[0]);
    }

    // Use the same logic as the main function
    if (assessment.groupedTasks && assessment.groupedTasks.length > 0) {
      const totalScore = assessment.groupedTasks.reduce(
        (sum, task) => sum + task.aggregated.score,
        0
      );
      const taskCount = assessment.groupedTasks.length;
      const result = Math.round((totalScore / taskCount) * 100);
      console.log(`Using groupedTasks: ${totalScore}/${taskCount} * 100 = ${result}`);
      return result;
    }

    if (assessment.tasks && assessment.tasks.length > 0) {
      const totalScore = assessment.tasks.reduce(
        (sum, task) => sum + task.score,
        0
      );
      const taskCount = assessment.tasks.length;
      const result = Math.round((totalScore / taskCount) * 100);
      console.log(`Using individual tasks: ${totalScore}/${taskCount} * 100 = ${result}`);
      return result;
    }

    console.log("No tasks found, returning 0");
    return 0;
  };

  const getReadingScoreClass = (score) => {
    if (score === 0) {
      return "low-color";
    } else if (score > 0 && score <= 20) {
      return "low-color";
    } else if (score > 20 && score <= 40) {
      return "med-color";
    } else if (score > 40 && score <= 80) {
      return "noRisk-color";
    } else if (score >= 81 && score <= 100) {
      // Include 81 in high-color
      return "high-color";
    }
    return "";
  };

  const showNestedData = (assessmentIndex) => {
    setVisibleAccordions((prev) => {
      const newVisible = new Set(prev);
      if (newVisible.has(assessmentIndex)) {
        newVisible.delete(assessmentIndex); // Close the clicked accordion
      } else {
        newVisible.add(assessmentIndex); // Open the clicked accordion
      }
      return newVisible; // Return the updated state
    });
  };

  const toggleNestedAccordion = (assessmentIndex, taskIndex) => {
    setVisibleNestedAccordions((prev) => {
      const key = `${assessmentIndex}-${taskIndex}`;
      const newState = { ...prev };
      if (newState[key]) {
        delete newState[key]; // Close the accordion
      } else {
        newState[key] = true; // Open the accordion
      }
      return newState;
    });
  };

  const sortData = (field) => {
    const sortField = field === "atRisk" ? "readingScore" : field;
    setSortCriteria((prev) => {
      const isAscending = prev.field === sortField && prev.order === "asc";
      return { field: sortField, order: isAscending ? "desc" : "asc" };
    });
  };

  const [sortCriteria, setSortCriteria] = useState({
    field: null,
    order: null,
  });

  const renderTaskScore = (groupedTasks, taskName) => {
    const group = groupedTasks.find((t) => t.name === taskName);
    if (!group) return "";

    const score = Math.round(group.aggregated.score * 100);
    let scoreClass = getReadingScoreClass(score);

    return <span className={`task-score ${scoreClass}`}>{score}%</span>;
  };

  const getSortIcon = (fieldName) => {
    if (sortCriteria.field === fieldName) {
      return sortCriteria.order === "asc"
        ? process.env.PUBLIC_URL + "/assets/imgs/desc-icon-active.png"
        : process.env.PUBLIC_URL + "/assets/imgs/asc-icon-active.png";
    }
    return process.env.PUBLIC_URL + "/assets/imgs/asc-icon-default.png";
  };

  const allEventKeys = assessmentResults.flatMap(
    (assessment, assessmentIndex) =>
      assessment.groupedTasks.map(
        (_, groupIndex) => `${assessmentIndex}-${groupIndex}`
      )
  );

  return (
    <div ref={currentViewRef} className="all-results-history-root">
      {" "}
      {/* Wrap the component in a div and assign the ref */}
      <Row>
        <Col>
          <div className="results-history-container">
            <div ref={overviewRef} className="print-only-header">
              <thead className="table-header print-thead table-header-gap">
                <tr>
                  <th
                    colSpan={taskNames.length + 5}
                    className="at-risk-filter-row"
                  >
                    <div className="at-risk-filter">
                      <img
                        src={
                          process.env.PUBLIC_URL + "/assets/imgs/0-20Bar.png"
                        }
                        alt="0-20%"
                        className="risk-bar"
                      />
                      <img
                        src={
                          process.env.PUBLIC_URL + "/assets/imgs/21-40Bar.png"
                        }
                        alt="21-40%"
                        className="risk-bar"
                      />
                    </div>
                  </th>
                </tr>
                <tr>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("name")}
                  >
                    <div className="header-content">Assessment</div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("readingScore")}
                  >
                    <div className="header-content">Overall Standard Score vs Test</div>
                  </th>
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("atRisk")}
                  >
                    <div className="header-content">At-Risk</div>
                  </th>
                  {taskNames.map((taskName) => (
                    <th
                      className="table-header-cell"
                      key={taskName}
                      onClick={() => sortData(taskName)}
                    >
                      <div className="header-content">{taskName}</div>
                    </th>
                  ))}
                  <th
                    className="table-header-cell"
                    onClick={() => sortData("totalTime")}
                  >
                    <div className="header-content">Time</div>
                  </th>
                  {!isPrintOut ? (
                    <th className="table-header-cell">
                      <div className="header-content">View</div>
                    </th>
                  ) : null}
                </tr>
              </thead>
            </div>

            <Accordion
              ref={detailedRef}
              className="print-detailed-view"
              alwaysOpen={isPrintOut || viewTeacher || viewParent}
              defaultActiveKey={
                isPrintOut
                  ? students.map((student) => student.id)
                  : viewTeacher
                    ? students.map((student) => student.id)
                    : viewParent
                      ? students.map((student) => student.id)
                      : []
              }
            >
              {students.map((student) => (
                <React.Fragment key={student.id}>
                  {/* Print-only header before each student accordion */}
                  <div className="alpaca-print-header print-only">
                    <div className="printout-logo" />
                    <div className="alpaca-print-header-text">
                      <span className="alpaca-print-header-year">
                        School Year: {academicYear}
                      </span>
                      <span className="alpaca-print-header-year">
                        Student Name: {student.name}
                      </span>
                      <span className="alpaca-print-header-powered">
                        Powered by ALPACA &copy; {new Date().getFullYear()}
                      </span>
                    </div>
                  </div>
                  <div
                    className="student-print-block break-after"
                    key={student.id}
                  >
                    <Accordion.Item
                      eventKey={student.id}
                      key={student.id}
                      className="avoid-page-break"
                    >
                      <Accordion.Header className="avoid-page-break">
                        {student.name}
                      </Accordion.Header>
                      <Accordion.Body className="avoid-page-break">
                        {detailedView && studentNotes[student.id] &&
                          studentNotes[student.id].length > 0 && (
                            <div style={{ marginBottom: "12px", textAlign: "left", ...(isPrintOut && { breakAfter: "page", pageBreakAfter: "always" }) }}>
                              <h6
                                style={{
                                  color: "#333",
                                  marginTop: 0,
                                  marginBottom: "12px",
                                  textAlign: "left",
                                }}
                              >
                                {student.name}'s Notes
                              </h6>

                              {studentNotes[student.id].map((note, idx) => (
                                <div
                                  key={`${student.id}-note-${idx}`}
                                  style={{
                                    marginBottom: "10px",
                                    padding: "8px 12px",
                                    borderLeft: "3px solid #1a56db",
                                    background: "#f8f9fa",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "left",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {/* <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        background: "#1a56db",
                                        color: "white",
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {note.note_type}
                                    </span> */}

                                    <span style={{ fontSize: "11px", color: "#666" }}>
                                      {getTeacherName(note.teacherid)} &bull; {formatNoteDate(note.date_created)}
                                    </span>
                                  </div>

                                  <div
                                    style={{
                                      fontSize: "13px",
                                      color: "#333",
                                      whiteSpace: "pre-wrap",
                                      textAlign: "left",
                                    }}
                                  >
                                    {note.note_text}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        <Table className="result-history-table">
                          <thead
                            className="table-header print-thead avoid-page-break table-header-gap"
                            style={{ marginBottom: "16px" }}
                          >
                            <tr className="table-row avoid-page-break">
                              <th
                                className="table-header-cell name-column"
                                onClick={() => sortData("name")}
                              >
                                <div>Screener</div>
                              </th>
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("readingScore")}
                              >
                                <div className="header-content">
                                  <span>Overall Standard Score vs Test</span>                                 
                                </div>
                              </th>

                              {taskNames.map((taskName) => (
                                <th
                                  className="table-header-cell"
                                  key={taskName}
                                  onClick={() => sortData(taskName)}
                                >
                                  <div className="header-content">
                                    <span>{taskName}</span>                                    
                                  </div>
                                </th>
                              ))}
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("atRisk")}
                              >
                                <div className="header-content">
                                  <span>At-Risk</span>     
                                </div>
                              </th>
                              <th
                                className="table-header-cell"
                                onClick={() => sortData("totalTime")}
                              >
                                <div className="header-content">
                                  <span>Time</span>
                                </div>
                              </th>
                              {!isPrintOut ? (
                                <th className="table-header-cell">
                                  <div className="header-content">View</div>
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody className="result-history-table-rows avoid-page-break table-body-gap">
                            {derivedResults
                              .filter(
                                (assessment) =>
                                  assessment.studentName === student.name
                              )
                              .map((assessment, assessmentIndex) => (
                                <React.Fragment key={assessment.globalAssessmentIndex}>
                                  <tr
                                    id={assessment.studentName
                                      .replace(/\s+/g, "-")
                                      .toLowerCase()}
                                    className="table-row avoid-page-break"
                                  >
                                    <td
                                      className="name-column report-cards-text all-results-history-td"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      {assessment.name}
                                    </td>
                                    <td className="centre-item task-badge-parent all-results-history-td">
                                      {assessment.readingScore ? (
                                        <span
                                          className={`reading-score ${getReadingScoreClass(
                                            assessment.readingScore
                                          )}`}
                                        >
                                          {assessment.readingScore}%
                                        </span>
                                      ) : null}
                                      <span className="tooltip">
                                        Average of all screener Overall Standard Score vs Test
                                        this year
                                      </span>
                                    </td>

                                    {taskNames.map((taskName) => (
                                      <td
                                        className="results-task-score centre-item task-badge-parent all-results-history-td"
                                        data-value={taskName
                                          .replace(/\s+/g, "-")
                                          .toLowerCase()}
                                        key={taskName}
                                      >
                                        {renderTaskScore(
                                          assessment.groupedTasks,
                                          taskName
                                        )}
                                      </td>
                                    ))}
                                    <td className="all-results-history-td">
                                      {assessment.readingScore !== undefined &&
                                        assessment.readingScore < 20 ? (
                                        <img
                                          src={
                                            process.env.PUBLIC_URL +
                                            "/assets/imgs/risk-flag-red.png"
                                          }
                                          className="flag-size-results"
                                          alt="Red Flag"
                                        />
                                      ) : assessment.readingScore >= 20 &&
                                        assessment.readingScore <= 40 ? (
                                        <img
                                          src={
                                            process.env.PUBLIC_URL +
                                            "/assets/imgs/risk-flag-yellow.png"
                                          }
                                          className="flag-size-results"
                                          alt="Yellow Flag"
                                        />
                                      ) : null}
                                    </td>
                                    <td
                                      className="results-task-score centre-item all-results-history-td"
                                      data-value={assessment.name}
                                    >
                                      {secondsToTime(assessment.totalTime)}
                                    </td>
                                    {!isPrintOut ? (
                                      <td
                                        className="all-results-history-td"
                                        data-toggle="toggle"
                                      >
                                        <img
                                          className="collapse-click"
                                          onClick={() =>
                                            showNestedData(assessment.globalAssessmentIndex)
                                          }
                                          src={
                                            visibleAccordions.has(
                                              assessment.globalAssessmentIndex
                                            )
                                              ? "../assets/imgs/minus-icon-small.png"
                                              : "../assets/imgs/plus-icon-small.png"
                                          }
                                        />
                                      </td>
                                    ) : null}
                                  </tr>
                                  {!hideAccordion && detailedView && (
                                    <tr
                                      colSpan="12"
                                      id={
                                        assessment.name
                                          .replace(/\s+/g, "-")
                                          .toLowerCase() + "-data"
                                      }
                                      className={`result-accordian-width ${visibleAccordions.has(
                                        assessment.globalAssessmentIndex
                                      ) || isPrintOut
                                        ? ""
                                        : "hide"
                                        } avoid-page-break`}
                                    >
                                      <td
                                        colSpan="12"
                                        className="avoid-page-break"
                                      >
                                        <Row>
                                          <Col className="text-left">
                                            {!isPrintOut && (
                                              <div className="retake-full-assessment-button-container">
                                                <button
                                                  className="gold-button"
                                                  onClick={() =>
                                                    handleRetakeFullAssessment(
                                                      assessment
                                                    )
                                                  }
                                                >
                                                  Retake{" "}
                                                  {assessment.studentName}
                                                </button>
                                              </div>
                                            )}
                                          </Col>
                                        </Row>
                                        <Accordion
                                          className="results-accordion avoid-page-break"
                                          activeKey={assessment.tasks
                                            .map(
                                              (_, taskIndex) =>
                                                `${assessment.globalAssessmentIndex}-${taskIndex}`
                                            )
                                            .filter(
                                              (key) =>
                                                visibleNestedAccordions[key]
                                            )}
                                        >
                                          {assessment.tasks.map(
                                            (task, taskIndex) => (
                                              <Accordion.Item
                                                eventKey={`${assessment.globalAssessmentIndex}-${taskIndex}`}
                                                key={taskIndex}
                                                style={{ border: "white" }}
                                                className="avoid-page-break"
                                              >
                                                <Accordion.Header
                                                  onClick={() =>
                                                    toggleNestedAccordion(
                                                      assessment.globalAssessmentIndex,
                                                      taskIndex
                                                    )
                                                  }
                                                  className="report-card-accordion-head avoid-page-break"
                                                >
                                                  <Row
                                                    className="task-results-table-row avoid-page-break"
                                                    style={{
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <Col
                                                      // className="col-10 align-left"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <button
                                                        className="delete-button"
                                                        style={{
                                                          marginRight: 16,
                                                        }}
                                                        onClick={(e) =>
                                                          handleDeleteTask(
                                                            e,
                                                            assessment.globalAssessmentIndex,
                                                            taskIndex
                                                          )
                                                        }
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={faRedo}
                                                        />
                                                      </button>
                                                    </Col>
                                                    <Col>
                                                      <span
                                                        style={{
                                                          whiteSpace: "nowrap",
                                                          // overflow: "hidden",

                                                          display:
                                                            "inline-block",
                                                          width: "100%",
                                                        }}
                                                      >
                                                        <i>
                                                          {task.name} (
                                                          {task.parentTaskName ||
                                                            task.name}
                                                          )
                                                        </i>
                                                      </span>
                                                    </Col>
                                                  </Row>
                                                </Accordion.Header>
                                                <Accordion.Body className="avoid-page-break">
                                                  {console.log("Task details:", task)}
                                                  {task.answers && task.answers.length > 0 ? (
                                                    <TaskResultsTable2
                                                      taskAnswers={task.answers}
                                                    />
                                                  ) : (
                                                    <p className="no-answers-message">No answer details available for this task.</p>
                                                  )}
                                                </Accordion.Body>
                                              </Accordion.Item>
                                            )
                                          )}
                                        </Accordion>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </div>
                </React.Fragment>
              ))}
            </Accordion>
          </div>
        </Col>
      </Row>
      <DeleteAnswerModal
        show={showModal}
        handleClose={handleModalClose}
        taskName={modalTask}
        studentName={modalAssessment}
        assessmentName={modalAssessment}
        handleDelete={() => { }}
        answerIds={answerIds}
        teacherid={teacherid}
        isRetakeFullAssessment={isRetakeFullAssessment}
      />
    </div>
  );
}

export default AllResultsHistory;
